import { type ReactNode, useId, useMemo, useState } from 'react';
import { format, isSameDay } from 'date-fns';

import { smoothPath } from '../lib/smoothPath';
import { useNow } from '../useNow';
import {
  aqiBand,
  condition,
  convertTemp,
  convertWind,
  fmtHour,
  fmtPrecip,
  fmtSnow,
  fmtTemp,
  fmtWind,
  pickAqi,
  WIND_LABEL,
  type HourData,
  type WeatherView
} from '../weather';
import { SkyRibbon, type SkyHour } from './WeekMeteogram';

// The row is a stack of lanes, each with its own vertical scale, placed in
// pixels from the row's top edge: the sky ribbon, temperature, precipitation,
// wind. Horizontally every lane shares the week, 00:00 at a day column's left
// edge to 24:00 at its right, so a time lines up with the columns of the hour
// grid above.
const ROW_H = 174;
const SKY_TOP = 5;
const SKY_H = 6;
const TEMP_TOP = 15;
const TEMP_H = 92;
// Room above and below the temperature lines for the high and low labels.
const TEMP_PAD = 15;
const PRECIP_TOP = 113;
const PRECIP_H = 28;
const WIND_TOP = 147;
const WIND_H = 22;

// The precipitation lane is linear and tops out at the week's wettest hour,
// or this when the week is drier, so a light shower doesn't fill the lane.
const PRECIP_FLOOR_MM = 4;
const PRECIP_MIN_MM = 0.1;
// Likewise for wind: the lane's top is the week's strongest gust rounded up,
// and at least this.
const WIND_FLOOR_KMH = 20;
const HOURS_IN_WEEK = 7 * 24;
// Extreme labels closer together than this (as a fraction of the week) would
// overlap; the less extreme of the two is dropped.
const LABEL_GAP = 0.04;

interface Hour {
  t: number;
  x: number; // 0..1 across the week
  d: HourData;
  rainMm: number;
  snowMm: number; // water equivalent
  snowCm: number;
}

interface Extreme {
  t: number;
  x: number;
  y: number;
  c: number;
}

interface Props {
  days: Date[];
  weather: WeatherView;
  gutter: number; // width of the time column, px
  onOpen?: (date: string, rect: DOMRect) => void;
}

// A meteogram for the week view: one row below the hour grid with the hourly
// sky, temperature and feels-like, precipitation, and wind and gusts. Hovering
// reads out every value for the hour under the pointer; clicking opens that
// day's weather.
export default function WeekWeatherRow({ days, weather, gutter, onOpen }: Props) {
  // useId's colons would need escaping inside url(#…).
  const gradientId = `sky${useId().replace(/:/g, '')}`;
  const now = useNow();
  const [hover, setHover] = useState<Hour | null>(null);
  const { prefs, hoursByDay } = weather;
  const tUnit = prefs.temperature_unit;
  const pUnit = prefs.precipitation_unit;
  const wUnit = prefs.wind_speed_unit;

  const hours = useMemo(() => {
    const out: Hour[] = [];
    days.forEach((day, col) => {
      for (const d of hoursByDay.get(format(day, 'yyyy-MM-dd')) ?? []) {
        const at = new Date(d.t * 1000);
        const precip = d.precipitation ?? 0;
        const snowCm = d.snowfall ?? 0;
        // Open-Meteo's rule of thumb: 7 cm of snow melts to 1 cm of water.
        const snowMm = Math.min(precip, (snowCm * 10) / 7);
        out.push({
          t: d.t,
          x: (col + (at.getHours() + at.getMinutes() / 60) / 24) / 7,
          d,
          rainMm: precip - snowMm,
          snowMm,
          snowCm
        });
      }
    });
    return out;
  }, [days, hoursByDay]);

  // Temperature and feels-like share one scale, with ticks at round values
  // of the display unit.
  const temp = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const h of hours) {
      for (const c of [h.d.temperature_2m, h.d.apparent_temperature]) {
        if (c == null) continue;
        const v = convertTemp(c, tUnit);
        lo = Math.min(lo, v);
        hi = Math.max(hi, v);
      }
    }
    if (lo > hi) return null;
    const span = Math.max(1, hi - lo);
    const yOfValue = (v: number) =>
      TEMP_TOP + TEMP_PAD + (1 - (v - lo) / span) * (TEMP_H - 2 * TEMP_PAD);
    const step = [1, 2, 5, 10, 20].find((s) => span / s <= 4) ?? 50;
    const ticks: { value: number; y: number }[] = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) {
      ticks.push({ value: v, y: yOfValue(v) });
    }
    return { y: (c: number) => yOfValue(convertTemp(c, tUnit)), ticks };
  }, [hours, tUnit]);

  const precipMax = useMemo(
    () =>
      Math.max(PRECIP_FLOOR_MM, Math.ceil(Math.max(0, ...hours.map((h) => h.rainMm + h.snowMm)))),
    [hours]
  );
  const bars = useMemo(() => hours.filter((h) => h.rainMm + h.snowMm >= PRECIP_MIN_MM), [hours]);

  const wind = useMemo(() => {
    let peak = convertWind(WIND_FLOOR_KMH, wUnit);
    let any = false;
    for (const h of hours) {
      for (const kmh of [h.d.wind_speed_10m, h.d.wind_gusts_10m]) {
        if (kmh == null) continue;
        any = true;
        peak = Math.max(peak, convertWind(kmh, wUnit));
      }
    }
    if (!any) return null;
    const max = niceCeil(peak);
    return {
      max,
      y: (kmh: number) => WIND_TOP + (1 - Math.min(1, convertWind(kmh, wUnit) / max)) * WIND_H
    };
  }, [hours, wUnit]);

  const lines = useMemo(
    () => ({
      temp: temp ? runs(hours, (h) => h.d.temperature_2m, temp.y) : [],
      feels: temp ? runs(hours, (h) => h.d.apparent_temperature, temp.y) : [],
      wind: wind ? runs(hours, (h) => h.d.wind_speed_10m, wind.y) : [],
      gusts: wind ? runs(hours, (h) => h.d.wind_gusts_10m, wind.y) : []
    }),
    [hours, temp, wind]
  );

  // Each day's highest and lowest reading, labelled on the line.
  const extremes = useMemo(() => {
    const highs: Extreme[] = [];
    const lows: Extreme[] = [];
    if (!temp) return { highs, lows };
    for (let col = 0; col < 7; col++) {
      let hi: Extreme | null = null;
      let lo: Extreme | null = null;
      for (const h of hours) {
        const c = h.d.temperature_2m;
        if (c == null || Math.floor(h.x * 7 + 1e-9) !== col) continue;
        const point = { t: h.t, x: h.x, y: temp.y(c), c };
        if (!hi || c > hi.c) hi = point;
        if (!lo || c < lo.c) lo = point;
      }
      if (hi) highs.push(hi);
      if (lo && hi && lo.t !== hi.t) lows.push(lo);
    }
    return {
      highs: spaced(highs, (a, b) => a.c >= b.c),
      lows: spaced(lows, (a, b) => a.c <= b.c)
    };
  }, [hours, temp]);

  const skyHours = useMemo<SkyHour[]>(
    () =>
      hours
        .filter((h) => h.d.cloud_cover != null)
        .map((h) => ({ t: h.t, x: h.x, cloud: h.d.cloud_cover!, isDay: h.d.is_day !== 0 })),
    [hours]
  );

  if (hours.length === 0) return null;

  const nowT = now.getTime() / 1000;
  const todayCol = days.findIndex((d) => isSameDay(d, now));
  const nowX =
    todayCol < 0 ? null : (todayCol + (now.getHours() + now.getMinutes() / 60) / 24) / 7;
  // Where "now" falls on the temperature line, between the readings around it.
  let nowY: number | null = null;
  if (temp && nowX != null) {
    for (let i = 0; i < hours.length - 1; i++) {
      const a = hours[i];
      const b = hours[i + 1];
      const ta = a.d.temperature_2m;
      const tb = b.d.temperature_2m;
      if (ta == null || tb == null || b.t - a.t > 3600) continue;
      if (a.t <= nowT && nowT < b.t) {
        nowY = temp.y(ta + ((tb - ta) * (nowT - a.t)) / (b.t - a.t));
        break;
      }
    }
  }

  function hourAt(ev: React.MouseEvent): { fx: number; rect: DOMRect } {
    const rect = ev.currentTarget.getBoundingClientRect();
    return { fx: (ev.clientX - rect.left) / rect.width, rect };
  }

  function onMove(ev: React.MouseEvent) {
    const { fx } = hourAt(ev);
    let nearest = hours[0];
    for (const h of hours) {
      if (Math.abs(h.x - fx) < Math.abs(nearest.x - fx)) nearest = h;
    }
    // Past the ends of the data there is nothing to read out.
    setHover(Math.abs(nearest.x - fx) <= 1 / HOURS_IN_WEEK ? nearest : null);
  }

  function onClick(ev: React.MouseEvent) {
    if (!onOpen) return;
    const { fx, rect } = hourAt(ev);
    const col = Math.max(0, Math.min(6, Math.floor(fx * 7)));
    const key = format(days[col], 'yyyy-MM-dd');
    if (!weather.data.days[key]) return;
    onOpen(key, new DOMRect(rect.left + (col * rect.width) / 7, rect.top, rect.width / 7, rect.height));
  }

  const slotW = 1000 / HOURS_IN_WEEK;
  const precipBase = PRECIP_TOP + PRECIP_H;
  const windBase = WIND_TOP + WIND_H;

  return (
    <div
      className="grid border-t border-[var(--border)] bg-[var(--bg)]"
      style={{ gridTemplateColumns: `${gutter}px minmax(0, 1fr)` }}
    >
      {/* Scale labels, in the time column */}
      <div
        aria-hidden
        className="relative border-r border-[var(--border)] text-[0.65rem] tabular-nums leading-none text-[var(--text-faint)]"
        style={{ height: ROW_H }}
      >
        {temp?.ticks.map((tick) => (
          <span key={tick.value} className="absolute right-2 -translate-y-1/2" style={{ top: tick.y }}>
            {tick.value}°
          </span>
        ))}
        <span className="absolute right-2" style={{ top: PRECIP_TOP }}>
          {pUnit === 'mm' ? `${precipMax} mm` : fmtPrecip(precipMax, pUnit)}
        </span>
        {wind && (
          <span className="absolute right-2" style={{ top: WIND_TOP }}>
            {wind.max} {WIND_LABEL[wUnit]}
          </span>
        )}
      </div>

      <div
        aria-hidden
        className={`relative ${onOpen ? 'cursor-pointer' : ''}`}
        style={{ height: ROW_H }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onClick={onClick}
      >
        {/* Day dividers, continuing the columns */}
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="pointer-events-none absolute bottom-0 top-0 border-r border-[var(--border)]"
            style={{ left: `calc(${((i + 1) / 7) * 100}% - 0.5px)` }}
          />
        ))}
        {/* Temperature gridlines and the two lane baselines */}
        {temp?.ticks.map((tick) => (
          <div
            key={tick.value}
            className="pointer-events-none absolute left-0 right-0 border-t border-[var(--border)] opacity-60"
            style={{ top: tick.y }}
          />
        ))}
        <div
          className="pointer-events-none absolute left-0 right-0 border-t border-[var(--border)]"
          style={{ top: precipBase }}
        />
        {wind && (
          <div
            className="pointer-events-none absolute left-0 right-0 border-t border-[var(--border)]"
            style={{ top: windBase }}
          />
        )}

        {skyHours.length > 1 && (
          <SkyRibbon hours={skyHours} id={gradientId} style={{ top: SKY_TOP, height: SKY_H }} gain={1.8} />
        )}

        <svg
          viewBox={`0 0 1000 ${ROW_H}`}
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 h-full w-full"
        >
          {bars.map((h) => {
            const total = h.rainMm + h.snowMm;
            const height = Math.max(1.5, (Math.min(total, precipMax) / precipMax) * PRECIP_H);
            const rainH = (height * h.rainMm) / total;
            const x = h.x * 1000 - slotW * 0.8;
            const w = slotW * 0.6;
            // What fell is drawn steady; a forecast fades with its chance.
            const prob = h.d.precipitation_probability;
            const opacity = h.t > nowT ? (prob == null ? 0.6 : 0.2 + (0.6 * prob) / 100) : 0.8;
            return (
              <g key={h.t} opacity={opacity}>
                {rainH > 0 && (
                  <rect x={x} y={precipBase - rainH} width={w} height={rainH} fill="var(--weather-rain)" />
                )}
                {height - rainH > 0 && (
                  <rect
                    x={x}
                    y={precipBase - height}
                    width={w}
                    height={height - rainH}
                    fill="var(--text-muted)"
                  />
                )}
              </g>
            );
          })}
          {lines.gusts.map((run) => (
            <path
              key={run[0][0]}
              d={`${smoothPath(run)}L${run[run.length - 1][0]} ${windBase}L${run[0][0]} ${windBase}Z`}
              fill="var(--text-muted)"
              fillOpacity={0.14}
            />
          ))}
          {lines.wind.map((run) => (
            <Line key={run[0][0]} run={run} stroke="var(--text-muted)" width={1.5} />
          ))}
          {lines.feels.map((run) => (
            <Line key={run[0][0]} run={run} stroke="var(--text-muted)" width={1} opacity={0.7} />
          ))}
          {lines.temp.map((run) => (
            <Line key={run[0][0]} run={run} stroke="var(--accent)" width={2} />
          ))}
        </svg>

        {extremes.highs.map((p) => (
          <ExtremeLabel key={p.t} point={p} above text={fmtTemp(p.c, tUnit)} />
        ))}
        {extremes.lows.map((p) => (
          <ExtremeLabel key={p.t} point={p} text={fmtTemp(p.c, tUnit)} />
        ))}

        {nowX != null && (
          <div
            className="pointer-events-none absolute bottom-0 top-0 w-px bg-[var(--today-fg)] opacity-70"
            style={{ left: `${nowX * 100}%` }}
          />
        )}
        {nowX != null && nowY != null && (
          <>
            <span
              className="wx-breathe pointer-events-none absolute h-[9px] w-[9px] rounded-full bg-[var(--accent)]"
              style={{ left: `${nowX * 100}%`, top: nowY }}
            />
            <Dot x={nowX} y={nowY} size={8} color="var(--accent)" />
          </>
        )}

        {hover && (
          <>
            <div
              className="pointer-events-none absolute bottom-0 top-0 w-px bg-[var(--text-faint)]"
              style={{ left: `${hover.x * 100}%` }}
            />
            {temp && hover.d.apparent_temperature != null && (
              <Dot x={hover.x} y={temp.y(hover.d.apparent_temperature)} size={6} color="var(--text-muted)" />
            )}
            {temp && hover.d.temperature_2m != null && (
              <Dot x={hover.x} y={temp.y(hover.d.temperature_2m)} size={8} color="var(--accent)" />
            )}
            {wind && hover.d.wind_speed_10m != null && (
              <Dot x={hover.x} y={wind.y(hover.d.wind_speed_10m)} size={6} color="var(--text-muted)" />
            )}
            <Readout hour={hover} future={hover.t > nowT} weather={weather} />
          </>
        )}
      </div>
    </div>
  );
}

function Line({
  run,
  stroke,
  width,
  opacity
}: {
  run: [number, number][];
  stroke: string;
  width: number;
  opacity?: number;
}) {
  return (
    <path
      d={smoothPath(run)}
      fill="none"
      stroke={stroke}
      strokeOpacity={opacity}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

// A marker on a line, ringed in the surface colour so it stays legible where
// lines cross under it.
function Dot({ x, y, size, color }: { x: number; y: number; size: number; color: string }) {
  return (
    <span
      className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{
        left: `${x * 100}%`,
        top: y,
        width: size,
        height: size,
        background: color,
        boxShadow: '0 0 0 2px var(--bg)'
      }}
    />
  );
}

function ExtremeLabel({ point, text, above = false }: { point: Extreme; text: string; above?: boolean }) {
  // Centred on the reading, except at the row's ends where it would be cut off.
  const shiftX = point.x < 0.015 ? '0' : point.x > 0.985 ? '-100%' : '-50%';
  return (
    <span
      className="pointer-events-none absolute text-[0.65rem] tabular-nums leading-none text-[var(--text-muted)]"
      style={{
        left: `${point.x * 100}%`,
        top: above ? point.y - 5 : point.y + 5,
        transform: `translate(${shiftX}, ${above ? '-100%' : '0'})`,
        textShadow: '0 0 3px var(--bg), 0 0 3px var(--bg)'
      }}
    >
      {text}
    </span>
  );
}

// Every value for one hour, raised above the row so the graph stays in view.
function Readout({ hour, future, weather }: { hour: Hour; future: boolean; weather: WeatherView }) {
  const { prefs } = weather;
  const d = hour.d;
  const rows: { key: ReactNode; label: string; value: string }[] = [];
  const lineKey = (color: string, height: number) => (
    <span className="block w-3" style={{ height, background: color }} />
  );
  const barKey = (color: string, opacity = 1) => (
    <span className="mx-auto block h-[9px] w-[4px]" style={{ background: color, opacity }} />
  );

  if (d.temperature_2m != null) {
    rows.push({
      key: lineKey('var(--accent)', 2),
      label: 'Temperature',
      value: fmtTemp(d.temperature_2m, prefs.temperature_unit)
    });
  }
  if (d.apparent_temperature != null) {
    rows.push({
      key: lineKey('var(--text-muted)', 1),
      label: 'Feels like',
      value: fmtTemp(d.apparent_temperature, prefs.temperature_unit)
    });
  }
  const precip: string[] = [];
  if (hour.rainMm >= PRECIP_MIN_MM) precip.push(fmtPrecip(hour.rainMm, prefs.precipitation_unit));
  if (hour.snowCm >= 0.1) precip.push(`${fmtSnow(hour.snowCm, prefs.precipitation_unit)} snow`);
  if (future && d.precipitation_probability != null) {
    precip.push(`${Math.round(d.precipitation_probability)}% chance`);
  }
  rows.push({
    key: barKey(hour.snowMm > hour.rainMm ? 'var(--text-muted)' : 'var(--weather-rain)'),
    label: 'Precipitation',
    value: precip.length ? precip.join(', ') : 'None'
  });
  if (d.cloud_cover != null) {
    rows.push({ key: null, label: 'Cloud cover', value: `${Math.round(d.cloud_cover)}%` });
  }
  if (d.wind_speed_10m != null) {
    rows.push({
      key: lineKey('var(--text-muted)', 1.5),
      label: 'Wind',
      value: fmtWind(d.wind_speed_10m, prefs.wind_speed_unit)
    });
  }
  if (d.wind_gusts_10m != null) {
    rows.push({
      key: barKey('var(--text-muted)', 0.3),
      label: 'Gusts',
      value: fmtWind(d.wind_gusts_10m, prefs.wind_speed_unit)
    });
  }
  if (d.uv_index != null) {
    rows.push({ key: null, label: 'UV index', value: String(Math.round(d.uv_index)) });
  }
  const aqi = pickAqi(d, prefs.aqi_scale);
  if (aqi != null) {
    rows.push({
      key: null,
      label: 'Air quality',
      value: `${Math.round(aqi)}, ${aqiBand(aqi, prefs.aqi_scale).toLowerCase()}`
    });
  }

  // Centred under the crosshair, sliding inward near the row's ends.
  const shiftX = hour.x < 0.12 ? '0' : hour.x > 0.88 ? '-100%' : '-50%';
  const sky = condition(d.weather_code)?.label;
  return (
    <div
      className="pointer-events-none absolute z-30 whitespace-nowrap rounded border border-[var(--border-strong)] bg-[var(--popover-bg)] px-[9px] py-[7px] text-[0.72rem] leading-tight text-[var(--text)] shadow-md"
      style={{ left: `${hour.x * 100}%`, bottom: ROW_H + 4, transform: `translateX(${shiftX})` }}
    >
      <div className="mb-[5px] flex items-baseline justify-between gap-4">
        <span className="font-medium">
          {format(new Date(hour.t * 1000), 'EEE')} {fmtHour(hour.t)}
        </span>
        {sky && <span className="text-[var(--text-muted)]">{sky}</span>}
      </div>
      <dl className="m-0 grid grid-cols-[12px_auto_auto] items-center gap-x-2 gap-y-[3px]">
        {rows.map((r) => (
          <div key={r.label} className="contents">
            <span>{r.key}</span>
            <dt className="text-[var(--text-muted)]">{r.label}</dt>
            <dd className="m-0 text-right tabular-nums">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// The hours' values as runs of [x, y] points in the row's 1000-wide SVG box.
// A missing hour ends a run rather than being bridged.
function runs(
  hours: Hour[],
  value: (h: Hour) => number | null,
  y: (v: number) => number
): [number, number][][] {
  const out: [number, number][][] = [];
  let current: [number, number][] = [];
  let prevT: number | null = null;
  for (const h of hours) {
    const v = value(h);
    if (v == null) continue;
    if (prevT != null && h.t - prevT > 3600 && current.length) {
      out.push(current);
      current = [];
    }
    current.push([h.x * 1000, y(v)]);
    prevT = h.t;
  }
  if (current.length) out.push(current);
  return out;
}

// Drops whichever of two neighbouring labels `better` ranks lower when they
// sit too close to both be read.
function spaced(points: Extreme[], better: (a: Extreme, b: Extreme) => boolean): Extreme[] {
  const out: Extreme[] = [];
  for (const p of [...points].sort((a, b) => a.x - b.x)) {
    const last = out[out.length - 1];
    if (last && p.x - last.x < LABEL_GAP) {
      if (better(p, last)) out[out.length - 1] = p;
    } else {
      out.push(p);
    }
  }
  return out;
}

function niceCeil(v: number): number {
  return [5, 10, 20, 30, 40, 50, 60, 80, 100, 150, 200].find((s) => s >= v) ?? Math.ceil(v / 100) * 100;
}
